import React from 'react';
import { FieldProps } from 'formik';
import { analyticsEvent } from '@/src/analytic_tag_manager';
import Select from './Select';
import SelectConsultationReason from './SelectConsultationReason';

type Props = {
  options: Array<Record<string, any>>;
  onReset?: () => void;
  onChange?: Function;
  defaultLabel: string;
  optionRender: (entity: any) => string;
  valueProperty: string;
  isCustom: boolean;
  showType: any;
  autoSelectIfOnlyOneOption?: boolean;
  id?: string;
  autoSelectCheck?: (...args: any[]) => boolean;
} & FieldProps;

const reportSelectEvent = (field: FieldProps['field']) => {
  switch (field.name) {
    case 'specialityId':
      analyticsEvent({
        category: 'Booking',
        action: 'SelectSpeciality',
      });
      break;
    case 'consultationReasonName':
      analyticsEvent({
        category: 'Booking',
        action: 'SelectConsultationReason',
      });
      break;
    default:
  }
};

const ProgressiveSelect = ({
  field,
  onReset,
  onChange,
  isCustom,
  autoSelectIfOnlyOneOption,
  autoSelectCheck,
  ...props
}: Props) => {
  const SelectComponent = isCustom ? SelectConsultationReason : Select;
  const onFieldChange = React.useCallback(
    event => {
      reportSelectEvent(field);
      onReset?.();
      if (onChange) {
        onChange(event);
      } else {
        field.onChange(event);
      }
    },
    [reportSelectEvent, onReset, onChange, field.onChange],
  );
  const disabled = React.useMemo(
    () =>
      autoSelectIfOnlyOneOption &&
      props?.options?.length === 1 &&
      (typeof autoSelectCheck === 'function'
        ? autoSelectCheck(props.options[0])
        : true),
    [props?.options, autoSelectIfOnlyOneOption],
  );
  React.useEffect(() => {
    if (
      autoSelectIfOnlyOneOption &&
      props?.options?.length === 1 &&
      (typeof autoSelectCheck === 'function'
        ? autoSelectCheck(props.options[0])
        : true)
    ) {
      const selectedValue = props.options[0][props.valueProperty ?? 'id'];
      if (field?.value && field?.value === selectedValue) return;
      setTimeout(() =>
        onFieldChange({ target: { value: selectedValue, name: field.name } }),
      );
    }
  }, [props?.options, onFieldChange, field?.value, autoSelectIfOnlyOneOption]);
  return (
    <SelectComponent
      field={field}
      onChange={onFieldChange}
      disabled={disabled}
      {...props}
    />
  );
};

export default ProgressiveSelect;
